import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';
import { AVATAR_CONSTANTS } from '@/@types/avatars';

interface ICreateFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onTitleChange: (title: string) => void;
  description: string;
  onDescriptionChange: (desc: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function CreateFolderDialog({
  open,
  onOpenChange,
  title,
  onTitleChange,
  description,
  onDescriptionChange,
  onSubmit,
  isSubmitting,
}: ICreateFolderDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Folder</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="folder-title">Title</Label>
            <Input
              id="folder-title"
              value={title}
              onChange={e => onTitleChange(e.target.value)}
              maxLength={AVATAR_CONSTANTS.MAX_FOLDER_TITLE_LENGTH}
              placeholder="Enter folder title"
            />
          </div>
          <div>
            <Label htmlFor="folder-description">Description (optional)</Label>
            <Textarea
              id="folder-description"
              value={description}
              onChange={e => onDescriptionChange(e.target.value)}
              maxLength={AVATAR_CONSTANTS.MAX_FOLDER_DESCRIPTION_LENGTH}
              placeholder="Enter folder description"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={!title.trim() || isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface IEditFolderDialogProps {
  editingFolder: { id: Id<'avatarFolders'>; title: string } | null;
  onEditingFolderChange: (folder: { id: Id<'avatarFolders'>; title: string } | null) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function EditFolderDialog({
  editingFolder,
  onEditingFolderChange,
  onSubmit,
  isSubmitting,
}: IEditFolderDialogProps) {
  return (
    <Dialog
      open={editingFolder !== null}
      onOpenChange={open => !open && onEditingFolderChange(null)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename Folder</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="edit-folder-title">Title</Label>
            <Input
              id="edit-folder-title"
              value={editingFolder?.title || ''}
              onChange={e =>
                onEditingFolderChange(
                  editingFolder ? { ...editingFolder, title: e.target.value } : null
                )
              }
              maxLength={AVATAR_CONSTANTS.MAX_FOLDER_TITLE_LENGTH}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onEditingFolderChange(null)}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={!editingFolder?.title.trim() || isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface IDeleteFolderDialogProps {
  deletingFolderId: Id<'avatarFolders'> | null;
  onOpenChange: (id: Id<'avatarFolders'> | null) => void;
  onConfirm: () => void;
}

export function DeleteFolderDialog({
  deletingFolderId,
  onOpenChange,
  onConfirm,
}: IDeleteFolderDialogProps) {
  return (
    <AlertDialog
      open={deletingFolderId !== null}
      onOpenChange={open => {
        if (!open) onOpenChange(null);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Folder</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this folder and all its images? This action cannot be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onConfirm}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
